/* ==========================================================================
   Bundle promo block (bpromo) — variant awareness. Plan §5a.

   Source of truth is the product form's hidden variant input — every Shopify
   theme has it, Focal and the Artemis theater alike:
     form[action*="/cart/add"] [name="id"]
   Watched with a `change` listener AND a MutationObserver (Focal sets it
   programmatically), `?variant=` in the URL as the fallback. No dependency on
   Focal's internal events or the Artemis colorway bus.

   On a variant change: the link's `?wheel=` moves to the new variant id and
   the image swaps to that variant's own picture (images key on colourway).

   D56 (2026-09-23): a CUSTOM variant that is not yet in the cart turns the
   block into a note ("add to cart first") with no link — the uploads live on
   this page. The cart is known from the server render (data-bpromo-in-cart)
   and kept current from Focal's cart:updated (detail.cart) / cart:refresh
   (re-fetch /cart.js) events, so adding the wheel restores the link without
   a reload.
   ========================================================================== */
(() => {
  'use strict';

  // C1 m6: Focal's product section renders the promo's liquid block three times on a wheel page, so this deferred
  // file runs three times; the first run already takes every block (all parsed by then), the rest stand down
  if (window.__bpromoRan) return;
  window.__bpromoRan = true;

  const blocks = Array.from(document.querySelectorAll('[data-bpromo]'));
  if (!blocks.length) return;

  const findInput = () =>
    document.querySelector('form[action*="/cart/add"] input[name="id"]:not([disabled])') ||
    document.querySelector('form[action*="/cart/add"] [name="id"]');

  // Which signal to trust: the URL when the theme keeps `?variant=` current
  // (Focal does, ahead of re-rendering the form), else the hidden input.
  function currentVariant(prefer) {
    const q = new URLSearchParams(location.search).get('variant');
    const input = findInput();
    const fromInput = input && input.value ? String(input.value) : null;
    if (prefer === 'url' && q) return String(q);
    if (prefer === 'input' && fromInput) return fromInput;
    return (q && String(q)) || fromInput;
  }

  function dataOf(block) {
    if (block._bpromoData) return block._bpromoData;
    let d = { copy: {}, custom: {}, variants: {} };
    try { d = JSON.parse(block.querySelector('[data-bpromo-data]').textContent); } catch (e) { /* server render stands */ }
    block._bpromoData = d;
    return d;
  }

  const inCart = (block, variantId) => (block.dataset.bpromoInCart || '').split(',').includes(String(variantId));

  // D56: link, or the "add to cart first" note. Idempotent — called on every
  // variant AND cart change.
  function setMode(block, variantId) {
    const d = dataOf(block);
    const v = d.variants[variantId] || {};
    const waiting = !!v.custom && !inCart(block, variantId);
    const text = (sel, s) => { const el = block.querySelector(sel); if (el && s != null && el.textContent !== s) el.textContent = s; };
    // v2 (spec §4): the shown variant in the cart -> the card says so and names the wheel (its own copy, when the page has it)
    const carted = !waiting && inCart(block, variantId) && d.cart && d.cart.title;
    const copy = waiting ? d.custom : carted ? d.cart : d.copy;
    const tag = block.querySelector('.bpromo__incart');
    if (tag) tag.hidden = !carted;
    text('.bpromo__title', copy.title);
    text('.bpromo__body', copy.body);
    text('.bpromo__cta-text', copy.cta);
    block.classList.toggle('bpromo--waiting', waiting);
    if (waiting) {
      block.removeAttribute('href');
      block.setAttribute('aria-disabled', 'true');
    } else {
      const builder = block.dataset.bpromoBuilder || '/pages/bundle-builder';
      block.setAttribute('href', `${builder}?wheel=${encodeURIComponent(variantId)}&src=pdp-${encodeURIComponent(block.dataset.bpromoHandle || '')}`);
      block.removeAttribute('aria-disabled');
    }
  }

  function setCart(lines) {
    if (!Array.isArray(lines)) return;
    for (const b of blocks) {
      const pid = String(b.dataset.bpromoProduct || '');
      b.dataset.bpromoInCart = lines.filter((l) => String(l.product_id) === pid).map((l) => String(l.variant_id)).join(',');
      setMode(b, b.dataset.bpromoVariant);
    }
  }

  function apply(block, variantId) {
    if (!variantId || block.dataset.bpromoVariant === variantId) return;
    block.dataset.bpromoVariant = variantId;
    setMode(block, variantId);

    const url = (dataOf(block).variants[variantId] || {}).img;
    const img = block.querySelector('.bpromo__img');
    if (!img || !url || img.getAttribute('src') === url) return;
    img.classList.add('is-swapping');
    const next = new Image();
    next.onload = () => {
      img.removeAttribute('srcset');
      img.src = url;
      requestAnimationFrame(() => img.classList.remove('is-swapping'));
    };
    next.onerror = () => img.classList.remove('is-swapping');
    next.src = url;
  }

  function sync(prefer) {
    const id = currentVariant(prefer);
    for (const b of blocks) apply(b, id);
  }

  // 1. change events bubble from any variant picker to the document
  document.addEventListener('change', (e) => {
    if (e.target && (e.target.name === 'id' || e.target.closest('form[action*="/cart/add"]'))) sync('input');
  });

  // 1b. themes that announce the variant (Focal: `variant:change` with
  //     detail.variant) — used when present, never required
  document.addEventListener('variant:change', (e) => {
    const v = e && e.detail && e.detail.variant;
    if (v && v.id) for (const b of blocks) apply(b, String(v.id));
  });

  // 2. programmatic updates to the hidden input (Focal rewrites its value)
  let observed = null;
  function observe() {
    const input = findInput();
    if (!input || input === observed) return;
    observed = input;
    new MutationObserver(() => sync('input')).observe(input, { attributes: true, attributeFilter: ['value'] });
    const desc = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    if (desc && desc.set) {
      Object.defineProperty(input, 'value', {
        configurable: true,
        get() { return desc.get.call(this); },
        set(v) { desc.set.call(this, v); sync('input'); },
      });
    }
  }
  observe();
  // the form may re-render (Focal's product-rerender) — re-attach to a new input
  new MutationObserver(() => observe()).observe(document.body, { childList: true, subtree: true });

  // 3. history changes (Focal updates ?variant= on selection)
  const push = history.pushState, replace = history.replaceState;
  history.pushState = function () { push.apply(this, arguments); sync('url'); };
  history.replaceState = function () { replace.apply(this, arguments); sync('url'); };
  window.addEventListener('popstate', () => sync('url'));

  // 4. the cart (D56): Focal announces every change with the cart payload, or
  //    asks for a refresh — then /cart.js is the truth.
  document.documentElement.addEventListener('cart:updated', (e) => {
    const cart = e && e.detail && e.detail.cart;
    if (cart && cart.items) setCart(cart.items);
  });
  document.documentElement.addEventListener('cart:refresh', () => {
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).then((c) => c && setCart(c.items)).catch(() => {});
  });
  // a waiting block is not a link: swallow the click so nothing navigates
  for (const b of blocks) b.addEventListener('click', (e) => { if (b.classList.contains('bpromo--waiting')) e.preventDefault(); });

  sync();
})();
